// src/components/pages/OAuthCallback.js
// Google OAuth callback handler

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spin, Result, Button } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { exchangeCodeForTokens } from '../../api/googleDrive';

function OAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const handleCallback = async () => {
      // Debug: log full URL to see what Google returned
      console.log('OAuth Callback URL:', window.location.href);
      console.log('Search params:', window.location.search);
      console.log('Hash:', window.location.hash);

      const code = searchParams.get('code');
      const error = searchParams.get('error');
      const state = searchParams.get('state'); // Original path to redirect back

      console.log('Extracted code:', code ? 'present' : 'missing');
      console.log('Extracted error:', error);

      if (error) {
        console.error('OAuth error:', error);
        setStatus('error');
        setErrorMessage(`Authorization failed: ${error}`);
        return;
      }

      if (!code) {
        setStatus('error');
        setErrorMessage('No authorization code received. Check console for URL details.');
        return;
      }

      try {
        console.log('Exchanging code for tokens...');
        await exchangeCodeForTokens(code);
        console.log('Tokens received and stored');

        setStatus('success');

        // Redirect back to original page after 2 seconds
        setTimeout(() => {
          const redirectPath = state || '/document-list';
          navigate(redirectPath);
        }, 2000);

      } catch (err) {
        console.error('Error exchanging code:', err);
        setStatus('error');
        setErrorMessage(err.message || 'Failed to complete authentication');
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  if (status === 'loading') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      }}>
        <Spin size="large" />
        <p style={{ marginTop: 20, color: '#666' }}>
          Completing Google authentication...
        </p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      }}>
        <Result
          status="error"
          icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
          title="Authentication Failed"
          subTitle={errorMessage}
          extra={[
            <Button
              type="primary"
              key="retry"
              onClick={() => navigate('/document-list')}
            >
              Go Back and Retry
            </Button>,
          ]}
        />
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    }}>
      <Result
        status="success"
        icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
        title="Google Connected Successfully!"
        subTitle="You can now generate documents using Google Drive. Redirecting..."
        extra={[
          <Spin key="spinner" size="small" style={{ marginRight: 10 }} />,
          <span key="text" style={{ color: '#666' }}>Redirecting in 2 seconds...</span>,
        ]}
      />
    </div>
  );
}

export default OAuthCallback;
